import { SessionService } from '@airtradeit/authentication';
import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { Observable, Subject, zip } from 'rxjs';
import { finalize, take, tap } from 'rxjs/operators';

import { Router } from '@angular/router';
import { AppRoutesConstant } from '@constants/app-routes.costant';
import { AffiliateOption } from '@shared/services/affiliate/_interfaces_/affiliate-options.interface';
import { LocalStorageService } from '@shared/services/local-storage/local-storage.service';
import { selectAffiliateOptionsData } from '@store/affiliate-options/affiliate-options.selectors';
import { selectSelectedAffiliateCode } from '@store/session/session.selectors';
import { AppState } from '@store/state';
import * as SessionActions from 'src/app/core/store/session/session.actions';

@Component({
  selector: 'app-affiliate-selection-modal',
  templateUrl: './affiliate-selection-modal.component.html',
  styleUrls: ['./affiliate-selection-modal.component.scss']
})
export class AffiliateSelectionModalComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  @Output() hideModal = new EventEmitter<void>();
  affiliateSelectedOption: AffiliateOption;
  affiliateOptionsForm: FormGroup;
  noResults: boolean;
  affiliateOptions$: Observable<AffiliateOption[]>;
  loading: boolean;
  private unsubscribeAll$: Subject<void> = new Subject();
  private initialAffiliateCode: string | null;

  constructor(
    @Inject(Store) private _store: Store<AppState>,
    @Inject(FormBuilder) private _fb: FormBuilder,
    @Inject(LocalStorageService) private _localStorageService: LocalStorageService,
    @Inject(SessionService) private _sessionService: SessionService
  ) {}

  ngOnInit(): void {
    this.affiliateOptions$ = this._store.select(selectAffiliateOptionsData);
    this.createAffiliateOptionsForm();
    this.setFormInitialValue();
  }

  createAffiliateOptionsForm(): void {
    this.affiliateOptionsForm = this._fb.group({ selected: null });
  }

  setFormInitialValue(): void {
    zip(this._store.select(selectSelectedAffiliateCode), this.affiliateOptions$)
      .pipe(take(1))
      .subscribe(([code, affiliates]) => {
        this.initialAffiliateCode = code;
        this.affiliateOptionsForm.reset({
          selected: this.getInitialValue(code, affiliates)
        });
      });
  }

  getInitialValue(code: string, affiliates: AffiliateOption[]): string | null {
    const selected: AffiliateOption = affiliates.length && affiliates.find(({ Code }) => Code === code);
    this.affiliateSelectedOption = selected;
    this._localStorageService.setSelectedAffiliate(JSON.stringify(selected));
    return selected ? selected.DisplayValue : null;
  }

  handleNoResults(noResults: boolean): void {
    this.noResults = noResults;
  }

  submitForm(): void {
    if (this.affiliateSelectedOption?.Code) {
      const payload = {
        CultureCode: this._localStorageService.getCulture(),
        SelectedAffiliate: { Code: this.affiliateSelectedOption.Code }
      };

      if (this.affiliateSelectedOption.Code === this.initialAffiliateCode) {
        this.onCloseModal();
        return;
      }

      this.loading = true;
      this._sessionService
        .updateSessionData(payload)
        .pipe(
          take(1),
          tap((data) => this._store.dispatch(SessionActions.setSession({ data }))),
          finalize(() => (this.loading = false))
        )
        .subscribe(() => {
          this.navigateToOverview();
        });
    }
  }

  navigateToOverview(): void {
    if (this.router.url === AppRoutesConstant.Overview) {
      location.reload();
    } else {
      this.router.navigate([AppRoutesConstant.Overview]);
      this.hideModal.emit();
    }
  }

  onCloseModal(): void {
    this.hideModal.emit();
  }

  handleSelect(match: TypeaheadMatch): void {
    this.affiliateSelectedOption = match.item;
    this._localStorageService.setSelectedAffiliate(JSON.stringify(match.item));
  }

  handleBlur(): void {
    this.affiliateOptionsForm.patchValue({ selected: this.affiliateSelectedOption.DisplayValue });
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }
}
